import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const QuantityEstimatorCalculator = () => {
    const content = article.find((item) => item.id === 7);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [calculationMethod, setCalculationMethod] = useState("risk"); // risk, capital, position

  const [inputs, setInputs] = useState({
    // Risk-based inputs
    accountSize: "10000",
    riskPercent: "2",
    entryPrice: "50.00",
    stopLoss: "45.00",
    
    // Capital-based inputs
    capitalAmount: "5000",
    capitalPercent: "100",
    
    // Position-based inputs
    positionSize: "5000",
    maxPositionPercent: "20",
    
    // Advanced inputs
    commission: "10.00",
    fees: "5.00",
    leverage: "1",
    volatility: "15",
    
    // Portfolio inputs
    portfolioValue: "50000",
    maxPortfolioRisk: "5",
    correlation: "0.3",
    
    // Common inputs
    currency: "1",
    sharePrice: "50.00"
  });

  const [portfolio, setPortfolio] = useState([
    { symbol: "AAPL", quantity: "10", price: "150.00", weight: "30" },
    { symbol: "MSFT", quantity: "5", price: "300.00", weight: "30" },
    { symbol: "GOOGL", quantity: "2", price: "2500.00", weight: "40" }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateQuantity();
    }
  }, [inputs, portfolio, calculationMethod, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handlePortfolioChange = (index, field, value) => {
    const updatedPortfolio = portfolio.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setPortfolio(updatedPortfolio);
  };

  const addPortfolioItem = () => {
    setPortfolio([...portfolio, { symbol: "", quantity: "", price: "", weight: "" }]);
  };

  const removePortfolioItem = (index) => {
    if (portfolio.length > 1) {
      setPortfolio(portfolio.filter((_, i) => i !== index));
    }
  };

  const calculateQuantity = () => {
    const currencyRate = parseFloat(inputs.currency) || 1;
    const sharePrice = parseFloat(inputs.sharePrice) || 0;
    const commission = parseFloat(inputs.commission) || 0;
    const fees = parseFloat(inputs.fees) || 0;
    const leverage = parseFloat(inputs.leverage) || 1;
    const volatility = parseFloat(inputs.volatility) || 0;

    let quantity = 0;
    let positionSize = 0;
    let riskAmount = 0;
    let methodDescription = "";

    switch (calculationMethod) {
      case "risk":
        const accountSize = parseFloat(inputs.accountSize) || 0;
        const riskPercent = parseFloat(inputs.riskPercent) || 0;
        const entryPrice = parseFloat(inputs.entryPrice) || 0;
        const stopLoss = parseFloat(inputs.stopLoss) || 0;

        riskAmount = (riskPercent / 100) * accountSize;
        const riskPerShare = entryPrice - stopLoss;
        
        if (riskPerShare > 0) {
          quantity = Math.floor(riskAmount / riskPerShare);
          positionSize = quantity * entryPrice;
        }
        
        methodDescription = `Risk-based: ${riskPercent}% of $${accountSize} = $${riskAmount.toFixed(2)} risk`;
        break;

      case "capital":
        const capitalAmount = parseFloat(inputs.capitalAmount) || 0;
        const capitalPercent = parseFloat(inputs.capitalPercent) || 0;

        positionSize = Math.min(capitalAmount, (capitalPercent / 100) * capitalAmount);
        
        if (sharePrice > 0) {
          quantity = Math.floor(positionSize / sharePrice);
        }
        
        methodDescription = `Capital-based: ${capitalPercent}% of $${capitalAmount} = $${positionSize.toFixed(2)} investment`;
        break;

      case "position":
        const maxPositionSize = parseFloat(inputs.positionSize) || 0;
        const maxPositionPercent = parseFloat(inputs.maxPositionPercent) || 0;
        const portfolioValue = parseFloat(inputs.portfolioValue) || 0;

        positionSize = Math.min(maxPositionSize, (maxPositionPercent / 100) * portfolioValue);
        
        if (sharePrice > 0) {
          quantity = Math.floor(positionSize / sharePrice);
        }
        
        methodDescription = `Position-based: Max $${maxPositionSize} or ${maxPositionPercent}% of portfolio`;
        break;
    }

    // Apply leverage
    const leveragedQuantity = quantity * leverage;
    const leveragedPositionSize = positionSize * leverage;

    // Calculate commission impact
    const totalCommission = commission + fees;
    const effectivePositionSize = leveragedPositionSize - totalCommission;

    // Risk metrics
    const riskPerShare = sharePrice * (volatility / 100);
    const positionRisk = riskPerShare * leveragedQuantity;
    const riskPercentage = (positionRisk / (parseFloat(inputs.accountSize) || leveragedPositionSize)) * 100;

    // Portfolio correlation impact
    const correlation = parseFloat(inputs.correlation) || 0;
    const diversifiedRisk = positionRisk * (1 - correlation);

    // Calculate portfolio weights and diversification
    const portfolioAnalysis = calculatePortfolioAnalysis();

    const resultData = {
      quantity: Math.floor(quantity),
      leveragedQuantity: Math.floor(leveragedQuantity),
      positionSize: positionSize * currencyRate,
      leveragedPositionSize: leveragedPositionSize * currencyRate,
      effectivePositionSize: effectivePositionSize * currencyRate,
      riskAmount: riskAmount * currencyRate,
      riskPerShare: riskPerShare * currencyRate,
      positionRisk: positionRisk * currencyRate,
      diversifiedRisk: diversifiedRisk * currencyRate,
      riskPercentage,
      sharePrice: sharePrice * currencyRate,
      commission: commission * currencyRate,
      fees: fees * currencyRate,
      totalCommission: totalCommission * currencyRate,
      leverage,
      volatility,
      correlation,
      currencyRate,
      calculationMethod,
      methodDescription,
      portfolioAnalysis
    };

    setResult(resultData);
  };

  const calculatePortfolioAnalysis = () => {
    const totalValue = portfolio.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      return sum + (quantity * price);
    }, 0);

    const weightedPortfolio = portfolio.map(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const weight = parseFloat(item.weight) || 0;
      const value = quantity * price;
      const actualWeight = totalValue > 0 ? (value / totalValue) * 100 : 0;
      
      return {
        ...item,
        value,
        actualWeight,
        targetWeight: weight,
        difference: actualWeight - weight
      };
    });

    const concentrationRatio = weightedPortfolio.length > 0 ? 
      Math.max(...weightedPortfolio.map(p => p.actualWeight)) : 0;

    return {
      totalValue,
      weightedPortfolio,
      concentrationRatio,
      isDiversified: concentrationRatio <= 40
    };
  };

  const calculateRiskScenarios = () => {
    if (!result) return [];

    const scenarios = [
      { priceChange: -20, label: "Severe Drop" },
      { priceChange: -10, label: "Major Drop" },
      { priceChange: -5, label: "Moderate Drop" },
      { priceChange: 0, label: "Current" },
      { priceChange: 5, label: "Moderate Gain" },
      { priceChange: 10, label: "Major Gain" },
      { priceChange: 20, label: "Substantial Gain" }
    ];

    return scenarios.map(scenario => {
      const priceChange = scenario.priceChange / 100;
      const newPrice = result.sharePrice * (1 + priceChange);
      const profitLoss = (newPrice - result.sharePrice) * result.leveragedQuantity;
      const percentChange = (profitLoss / result.leveragedPositionSize) * 100;
      
      return {
        ...scenario,
        newPrice,
        profitLoss,
        percentChange,
        isLoss: profitLoss < 0
      };
    });
  };

  const calculateLeverageAnalysis = () => {
    if (!result) return [];

    const leverageLevels = [1, 2, 3, 5, 10];
    
    return leverageLevels.map(leverage => {
      const leveragedQty = result.quantity * leverage;
      const leveragedSize = result.positionSize * leverage;
      const leveragedRisk = result.positionRisk * leverage;
      const marginRequired = leveragedSize / 2; // Assuming 50% margin
      const riskPercentage = (leveragedRisk / (parseFloat(inputs.accountSize) || leveragedSize)) * 100;

      return {
        leverage,
        quantity: leveragedQty,
        positionSize: leveragedSize,
        risk: leveragedRisk,
        marginRequired,
        riskPercentage,
        isSafe: riskPercentage <= 10 // Conservative threshold
      };
    });
  };

  const resetCalculator = () => {
    setPortfolio([
      { symbol: "AAPL", quantity: "10", price: "150.00", weight: "30" },
      { symbol: "MSFT", quantity: "5", price: "300.00", weight: "30" },
      { symbol: "GOOGL", quantity: "2", price: "2500.00", weight: "40" }
    ]);
    setInputs({
      accountSize: "10000",
      riskPercent: "2",
      entryPrice: "50.00",
      stopLoss: "45.00",
      capitalAmount: "5000",
      capitalPercent: "100",
      positionSize: "5000",
      maxPositionPercent: "20",
      commission: "10.00",
      fees: "5.00",
      leverage: "1",
      volatility: "15",
      portfolioValue: "50000",
      maxPortfolioRisk: "5",
      correlation: "0.3",
      currency: "1",
      sharePrice: "50.00"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Recommended Quantity,${result.quantity}`,
      `With Leverage,${result.leveragedQuantity}`,
      `Position Size,${result.positionSize.toFixed(2)}`,
      `Leveraged Size,${result.leveragedPositionSize.toFixed(2)}`,
      `Risk Amount,${result.riskAmount.toFixed(2)}`,
      `Position Risk,${result.positionRisk.toFixed(2)}`,
      `Risk Percentage,${result.riskPercentage.toFixed(2)}%`,
      `Share Price,${result.sharePrice.toFixed(2)}`,
      `Commission & Fees,${result.totalCommission.toFixed(2)}`,
      `Leverage,${result.leverage}x`,
      `Calculation Method,${result.calculationMethod}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quantity-estimation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      portfolio: [...portfolio],
      calculationMethod,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setPortfolio(calc.portfolio || []);
    setCalculationMethod(calc.calculationMethod || "risk");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        calculationMethod: "risk",
        inputs: { 
          accountSize: "25000", 
          riskPercent: "1",
          leverage: "1",
          volatility: "10"
        }
      },
      moderate: { 
        calculationMethod: "capital",
        inputs: { 
          capitalAmount: "10000", 
          capitalPercent: "25",
          leverage: "2",
          volatility: "15"
        }
      },
      aggressive: { 
        calculationMethod: "position",
        inputs: { 
          positionSize: "20000", 
          maxPositionPercent: "40",
          leverage: "3",
          volatility: "25"
        }
      }
    };
    
    const presetData = presets[preset];
    setCalculationMethod(presetData.calculationMethod);
    setInputs(prev => ({ ...prev, ...presetData.inputs }));
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const allocationChartData = result?.portfolioAnalysis ? {
    labels: result.portfolioAnalysis.weightedPortfolio.map(p => p.symbol || 'Unknown'),
    datasets: [{
      data: result.portfolioAnalysis.weightedPortfolio.map(p => p.actualWeight),
      backgroundColor: [
        "#19692C", "#28A745", "#2B8C3A", "#1A7C2A", "#FFD93D",
        "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7"
      ],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const riskScenarioData = calculateRiskScenarios();
  const riskChartData = riskScenarioData.length > 0 ? {
    labels: riskScenarioData.map(s => s.label),
    datasets: [{
      label: 'Profit/Loss',
      data: riskScenarioData.map(s => s.profitLoss),
      backgroundColor: riskScenarioData.map(s => 
        s.isLoss ? '#E53E3E' : '#28A745'
      ),
      borderColor: riskScenarioData.map(s => 
        s.isLoss ? '#E53E3E' : '#28A745'
      ),
      borderWidth: 2
    }]
  } : null;

  const leverageData = calculateLeverageAnalysis();
  const leverageChartData = leverageData.length > 0 ? {
    labels: leverageData.map(l => `${l.leverage}x`),
    datasets: [
      {
        label: 'Position Size',
        data: leverageData.map(l => l.positionSize),
        backgroundColor: "#19692C",
        borderColor: "#19692C",
        borderWidth: 2
      },
      {
        label: 'Risk Percentage',
        data: leverageData.map(l => l.riskPercentage),
        backgroundColor: "#FF6B6B",
        borderColor: "#FF6B6B",
        borderWidth: 2,
        yAxisID: 'y1'
      }
    ]
  } : null;

  const positionBreakdownData = result ? {
    labels: ["Stock Value", "Commission & Fees"],
    datasets: [{
      data: [result.effectivePositionSize, result.totalCommission],
      backgroundColor: ["#19692C", "#FF6B6B"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Quantity Estimator Calculator</h1>
        </div>
        <p>Calculate optimal position sizes, manage risk exposure, and optimize portfolio allocation</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        {calc.quantity} shares
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Quantity Estimator'}
              </button>
              <button 
                className={`tab ${activeTab === "portfolio" ? "active" : ""}`}
                onClick={() => setActiveTab("portfolio")}
              >
                <i className="fas fa-chart-pie"></i>
                {isMobile ? 'Portfolio' : 'Portfolio Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Strategy Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculation Method Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${calculationMethod === "risk" ? "active" : ""}`}
                onClick={() => setCalculationMethod("risk")}
              >
                <i className="fas fa-shield-alt"></i>
                Risk-Based
              </button>
              <button 
                className={`mode-btn ${calculationMethod === "capital" ? "active" : ""}`}
                onClick={() => setCalculationMethod("capital")}
              >
                <i className="fas fa-money-bill-wave"></i>
                Capital-Based
              </button>
              <button 
                className={`mode-btn ${calculationMethod === "position" ? "active" : ""}`}
                onClick={() => setCalculationMethod("position")}
              >
                <i className="fas fa-layer-group"></i>
                Position-Based
              </button>
            </div>
          </div>

          {/* Quantity Estimator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Risk-Based Inputs */}
                  {calculationMethod === "risk" && (
                    <>
                      <h3>
                        <i className="fas fa-shield-alt"></i>
                        Risk Management
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-piggy-bank"></i> Account Size ($)</label>
                          <input 
                            type="number" 
                            name="accountSize" 
                            value={inputs.accountSize} 
                            onChange={handleChange}
                            placeholder="10000"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-exclamation-triangle"></i> Risk per Trade (%)</label>
                          <input 
                            type="number" 
                            name="riskPercent" 
                            value={inputs.riskPercent} 
                            onChange={handleChange}
                            placeholder="2"
                            step="0.1"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-dollar-sign"></i> Entry Price</label>
                          <input 
                            type="number" 
                            name="entryPrice" 
                            value={inputs.entryPrice} 
                            onChange={handleChange}
                            placeholder="50.00"
                            step="0.01"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                          <input 
                            type="number" 
                            name="stopLoss" 
                            value={inputs.stopLoss} 
                            onChange={handleChange}
                            placeholder="45.00"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Capital-Based Inputs */}
                  {calculationMethod === "capital" && (
                    <>
                      <h3>
                        <i className="fas fa-money-bill-wave"></i>
                        Capital Allocation
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-wallet"></i> Available Capital ($)</label>
                          <input 
                            type="number" 
                            name="capitalAmount" 
                            value={inputs.capitalAmount} 
                            onChange={handleChange}
                            placeholder="5000"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-percentage"></i> Capital to Use (%)</label>
                          <input 
                            type="number" 
                            name="capitalPercent" 
                            value={inputs.capitalPercent} 
                            onChange={handleChange}
                            placeholder="100"
                            step="1"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Position-Based Inputs */}
                  {calculationMethod === "position" && (
                    <>
                      <h3>
                        <i className="fas fa-layer-group"></i>
                        Position Sizing
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-chart-bar"></i> Max Position Size ($)</label>
                          <input 
                            type="number" 
                            name="positionSize" 
                            value={inputs.positionSize} 
                            onChange={handleChange}
                            placeholder="5000"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-percentage"></i> Max Portfolio %</label>
                          <input 
                            type="number" 
                            name="maxPositionPercent" 
                            value={inputs.maxPositionPercent} 
                            onChange={handleChange}
                            placeholder="20"
                            step="1"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-piggy-bank"></i> Portfolio Value ($)</label>
                          <input 
                            type="number" 
                            name="portfolioValue" 
                            value={inputs.portfolioValue} 
                            onChange={handleChange}
                            placeholder="50000"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Stock Details */}
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Stock Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Share Price</label>
                      <input 
                        type="number" 
                        name="sharePrice" 
                        value={inputs.sharePrice} 
                        onChange={handleChange}
                        placeholder="50.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="1">1x (No Leverage)</option>
                        <option value="2">2x</option>
                        <option value="3">3x</option>
                        <option value="5">5x</option>
                        <option value="10">10x</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Volatility (%)</label>
                      <input 
                        type="number" 
                        name="volatility" 
                        value={inputs.volatility} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-link"></i> Portfolio Correlation</label>
                      <input 
                        type="number" 
                        name="correlation" 
                        value={inputs.correlation} 
                        onChange={handleChange}
                        placeholder="0.3"
                        step="0.1"
                        min="0"
                        max="1"
                      />
                    </div>
                  </div>

                  {/* Costs */}
                  <h3>
                    <i className="fas fa-receipt"></i>
                    Costs & Fees
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Commission</label>
                      <input 
                        type="number" 
                        name="commission" 
                        value={inputs.commission} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Other Fees</label>
                      <input 
                        type="number" 
                        name="fees" 
                        value={inputs.fees} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Settings */}
                  <h3>
                    <i className="fas fa-cog"></i>
                    Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Portfolio Analysis Tab */}
          {activeTab === "portfolio" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-pie"></i>
                  Portfolio Allocation
                </h3>
                
                {portfolio.map((item, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <h4>Holding #{index + 1}</h4>
                      {portfolio.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removePortfolioItem(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-tag"></i> Symbol</label>
                        <input 
                          type="text" 
                          value={item.symbol}
                          onChange={(e) => handlePortfolioChange(index, 'symbol', e.target.value)}
                          placeholder="AAPL"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-layer-group"></i> Quantity</label>
                        <input 
                          type="number" 
                          value={item.quantity}
                          onChange={(e) => handlePortfolioChange(index, 'quantity', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Price</label>
                        <input 
                          type="number" 
                          value={item.price}
                          onChange={(e) => handlePortfolioChange(index, 'price', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-percentage"></i> Target Weight (%)</label>
                        <input 
                          type="number" 
                          value={item.weight}
                          onChange={(e) => handlePortfolioChange(index, 'weight', e.target.value)}
                          placeholder="0"
                          step="1"
                        />
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addPortfolioItem}>
                  <i className="fas fa-plus"></i>
                  Add Another Holding
                </button>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-layer-group"></i>
                          {calc.quantity} shares
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Size: ${calc.positionSize?.toFixed(2)}</span>
                        <span><i className="fas fa-shield-alt"></i> Risk: {calc.riskPercentage?.toFixed(2)}%</span>
                        <span><i className="fas fa-chess-board"></i> Method: {calc.calculationMethod}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "portfolio") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-cube"></i>
                    Quantity Estimation Results
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          {result.quantity} shares
                        </div>
                        <div className="result-label">
                          <i className="fas fa-cube"></i>
                          Recommended Quantity
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.leveragedQuantity} shares
                        </div>
                        <div className="result-label">
                          <i className="fas fa-rocket"></i>
                          With Leverage
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.positionSize.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-bar"></i>
                          Position Size
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.riskPercentage.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-shield-alt"></i>
                          Risk Exposure
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Share Price:</span>
                        <span>${result.sharePrice.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill-wave"></i> Effective Position Size:</span>
                        <span>${result.effectivePositionSize.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Commission & Fees:</span>
                        <span>${result.totalCommission.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bolt"></i> Leverage:</span>
                        <span>{result.leverage}x</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Volatility:</span>
                        <span>{result.volatility}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-link"></i> Portfolio Correlation:</span>
                        <span>{result.correlation}</span>
                      </div>
                    </div>

                    {/* Risk Management Summary */}
                    <div className="risk-summary">
                      <h4>Risk Analysis</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.riskAmount.toFixed(2)}
                          </div>
                          <div className="risk-label">Risk Amount</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.positionRisk.toFixed(2)}
                          </div>
                          <div className="risk-label">Position Risk</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.diversifiedRisk.toFixed(2)}
                          </div>
                          <div className="risk-label">Diversified Risk</div>
                        </div>
                      </div>
                    </div>

                    {/* Method Description */}
                    <div className="risk-summary">
                      <h4>Calculation Method</h4>
                      <div className="method-description">
                        <i className="fas fa-info-circle"></i>
                        {result.methodDescription}
                      </div>
                    </div>

                    {/* Position Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Position Cost Breakdown
                      </h3>
                      <Doughnut 
                        data={positionBreakdownData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Risk Scenario Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Risk Scenario Analysis
                      </h3>
                      <Bar 
                        data={riskChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Leverage Analysis Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-rocket"></i>
                        Leverage Impact Analysis
                      </h3>
                      <Bar 
                        data={leverageChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              title: {
                                display: true,
                                text: 'Position Size ($)'
                              }
                            },
                            y1: {
                              position: 'right',
                              title: {
                                display: true,
                                text: 'Risk Percentage (%)'
                              },
                              grid: {
                                drawOnChartArea: false
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Leverage Safety Analysis */}
                    <div className="risk-summary">
                      <h4>Leverage Safety Analysis</h4>
                      <div className="leverage-list">
                        {leverageData.map((level, index) => (
                          <div key={index} className={`leverage-item ${level.isSafe ? 'safe' : 'risky'}`}>
                            <div className="leverage-header">
                              <span className="leverage-level">{level.leverage}x Leverage</span>
                              <span className={`leverage-status ${level.isSafe ? 'positive' : 'negative'}`}>
                                {level.isSafe ? 'SAFE' : 'RISKY'}
                              </span>
                            </div>
                            <div className="leverage-details">
                              <span>Quantity: {Math.floor(level.quantity)} shares</span>
                              <span>Size: ${level.positionSize.toFixed(2)}</span>
                              <span>Risk: {level.riskPercentage.toFixed(2)}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-cube fa-3x"></i>
                    <p>Enter your parameters to calculate optimal quantity</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "portfolio" && result?.portfolioAnalysis && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-pie"></i>
                  Portfolio Analysis
                </h3>
                <div className="chart-container">
                  <Doughnut 
                    data={allocationChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: isMobile ? 'top' : 'right'
                        }
                      }
                    }}
                  />
                </div>
                
                <div className="portfolio-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${result.portfolioAnalysis.totalValue.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-piggy-bank"></i>
                      Total Portfolio Value
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {result.portfolioAnalysis.concentrationRatio.toFixed(1)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-pie"></i>
                      Concentration Ratio
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className={`summary-value ${result.portfolioAnalysis.isDiversified ? 'positive' : 'negative'}`}>
                      {result.portfolioAnalysis.isDiversified ? 'DIVERSIFIED' : 'CONCENTRATED'}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-balance-scale"></i>
                      Diversification Status
                    </div>
                  </div>
                </div><br/>

                <div className="portfolio-details">
                  <h4>Portfolio Holdings</h4>
                  <div className="holdings-list">
                    {result.portfolioAnalysis.weightedPortfolio.map((holding, index) => (
                      <div key={index} className="holding-item">
                        <div className="holding-header">
                          <span className="holding-symbol">{holding.symbol || 'Unknown'}</span>
                          <span className="holding-value">${holding.value.toFixed(2)}</span>
                        </div>
                        <div className="holding-details">
                          <span>Actual: {holding.actualWeight.toFixed(1)}%</span>
                          <span>Target: {holding.targetWeight}%</span>
                          <span className={`weight-difference ${holding.difference >= 0 ? 'positive' : 'negative'}`}>
                            {holding.difference >= 0 ? '+' : ''}{holding.difference.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default QuantityEstimatorCalculator;